import api from './api';

class ReschedulingService {
  /**
   * Suggest rescheduling options when scheduling would cause conflicts
   * @param {Object} eventData - The new event to schedule
   * @returns {Object} Rescheduling plan with suggestions
   */
  async suggestRescheduling(eventData) {
    try {
      const response = await api.post('/api/scheduling/suggest-rescheduling', eventData);
      return response.data;
    } catch (error) {
      console.error('Error suggesting rescheduling:', error);
      throw error;
    }
  }

  /**
   * Schedule an event with automatic rescheduling of conflicts
   * @param {Object} eventData - The new event to schedule
   * @param {boolean} executeNow - Whether to execute the rescheduling immediately
   * @returns {Object} Scheduling result with rescheduling details
   */
  async scheduleWithRescheduling(eventData, executeNow = false) {
    try {
      const requestData = {
        ...eventData,
        auto_reschedule: true,
        execute_plan: executeNow
      };
      
      const response = await api.post('/api/scheduling/schedule-with-rescheduling', requestData);
      return response.data;
    } catch (error) {
      console.error('Error scheduling with rescheduling:', error);
      throw error;
    }
  }

  /**
   * Check if a specific time slot has conflicts
   * @param {Object} timeSlotData - The time slot to check
   * @returns {Object} Availability check result with alternative suggestions
   */
  async checkTimeSlotAvailability(timeSlotData) {
    try {
      const response = await api.post('/api/scheduling/check-availability', timeSlotData);
      return response.data;
    } catch (error) {
      console.error('Error checking time slot availability:', error);
      throw error;
    }
  }

  /**
   * Format rescheduling suggestions for display
   * @param {Array} suggestions - Array of rescheduling suggestions
   * @returns {Array} Formatted suggestions with user-friendly information
   */
  formatSuggestionsForDisplay(suggestions) {
    return suggestions.map(suggestion => ({
      ...suggestion,
      originalTimeFormatted: this.formatDateTime(suggestion.original_start),
      suggestedTimeFormatted: this.formatDateTime(suggestion.suggested_start),
      impactLevel: this.getImpactLevel(suggestion.impact_score),
      confidenceLevel: this.getConfidenceLevel(suggestion.confidence),
      reasonFormatted: this.formatReason(suggestion.reason)
    }));
  }

  /**
   * Format datetime for user display
   * @param {string} dateTimeString - ISO datetime string
   * @returns {string} Formatted datetime
   */
  formatDateTime(dateTimeString) {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Get impact level description
   * @param {number} impactScore - Impact score from 0.0 to 1.0
   * @returns {Object} Impact level info
   */
  getImpactLevel(impactScore) {
    if (impactScore <= 0.3) {
      return { level: 'low', color: 'text-green-600', description: 'Minimal disruption' };
    } else if (impactScore <= 0.6) {
      return { level: 'medium', color: 'text-yellow-600', description: 'Moderate disruption' };
    } else {
      return { level: 'high', color: 'text-red-600', description: 'Significant disruption' };
    }
  }

  /**
   * Get confidence level description
   * @param {number} confidence - Confidence score from 0.0 to 1.0
   * @returns {Object} Confidence level info
   */
  getConfidenceLevel(confidence) {
    if (confidence >= 0.8) {
      return { level: 'high', color: 'text-green-600', description: 'High confidence' };
    } else if (confidence >= 0.6) {
      return { level: 'medium', color: 'text-yellow-600', description: 'Medium confidence' };
    } else {
      return { level: 'low', color: 'text-red-600', description: 'Low confidence' };
    }
  }

  /**
   * Format reason code into user-friendly text
   * @param {string} reasonCode - Reason code from the API
   * @returns {string} User-friendly reason
   */
  formatReason(reasonCode) {
    const reasonMap = {
      'moved_to_next_day': 'Moved to the next day',
      'moved_within_week': 'Moved within the same week',
      'moved_to_later_date': 'Moved to a later date',
      'moved_to_previous_day': 'Moved to the previous day',
      'moved_to_earlier_date': 'Moved to an earlier date',
      'moved_later_same_day': 'Moved later the same day',
      'moved_earlier_same_day': 'Moved earlier the same day',
      'no_alternatives_found': 'No suitable alternatives found',
      'close_to_preferred_time': 'Close to preferred time',
      'same_day_as_preferred': 'Same day as preferred',
      'today_available': 'Available today',
      'tomorrow_available': 'Available tomorrow',
      'next_available_slot': 'Next available time slot'
    };
    
    return reasonMap[reasonCode] || reasonCode;
  }

  /**
   * Calculate total disruption score for a rescheduling plan
   * @param {Array} suggestions - Array of rescheduling suggestions
   * @returns {Object} Disruption analysis
   */
  analyzeDisruption(suggestions) {
    if (!suggestions || suggestions.length === 0) {
      return {
        totalImpact: 0,
        averageImpact: 0,
        highImpactCount: 0,
        recommendation: 'proceed'
      };
    }

    const totalImpact = suggestions.reduce((sum, s) => sum + s.impact_score, 0);
    const averageImpact = totalImpact / suggestions.length;
    const highImpactCount = suggestions.filter(s => s.impact_score > 0.6).length;

    let recommendation;
    if (averageImpact <= 0.3) {
      recommendation = 'proceed';
    } else if (averageImpact <= 0.6) {
      recommendation = 'caution';
    } else {
      recommendation = 'reconsider';
    }

    return {
      totalImpact,
      averageImpact,
      highImpactCount,
      recommendation,
      affectedTasksCount: suggestions.length
    };
  }

  /**
   * Get recommendation message based on disruption analysis
   * @param {Object} disruption - Disruption analysis result
   * @returns {Object} Recommendation with styling
   */
  getRecommendationMessage(disruption) {
    switch (disruption.recommendation) {
      case 'proceed':
        return {
          message: `Minimal disruption expected. ${disruption.affectedTasksCount} task(s) can be easily rescheduled.`,
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          icon: '✓'
        };
      case 'caution':
        return {
          message: `Moderate disruption. ${disruption.affectedTasksCount} task(s) will be rescheduled with some impact.`,
          color: 'text-yellow-700',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          icon: '⚠'
        };
      case 'reconsider':
        return {
          message: `High disruption expected. Consider choosing a different time to minimize impact on ${disruption.affectedTasksCount} task(s).`,
          color: 'text-red-700',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          icon: '⚠'
        };
      default:
        return {
          message: 'Unable to analyze disruption.',
          color: 'text-gray-700',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          icon: '?'
        };
    }
  }
}

const reschedulingServiceInstance = new ReschedulingService();
export default reschedulingServiceInstance;